import PropTypes from 'prop-types';
import React from 'react';

import useTimeOfUsable from 'src/util/i18n/timeOfUse/useTimeOfUsable';

/**
 * Renders a time-of-usable entity as a single inline span with price/rate, details, and optional
 * conditions.
 *
 * **Use this component when:** You need to display time-of-use information inline within text,
 * tables, or compact layouts where horizontal space is limited and all information should appear
 * on a single line.
 *
 * **Key differences from Detailed:**
 * - Renders all content inline within a single `<span>` element separated by spaces
 * - All information (value, description, conditions) appears on the same line
 * - Suitable for tables, inline text, or compact layouts where vertical space is constrained
 * - Returns a single span element containing space-separated content
 * @param {object} props - Component props
 * @param {object} props.timeOfUsable - The time-of-usable entity containing pricing and time-of-use
 *   configuration.
 * @param {boolean} props.showConditions - Whether to display additional conditions for the clause
 * @param {string} [props.valueField='price'] - The value to be converted and formatted as a
 *   price or rate
 * @returns {React.ReactElement|null} A span element containing the formatted clause information
 *   with price, time-of-use description, and optional conditions, or null if timeOfUsable is empty
 */
function DetailedInline({ timeOfUsable, showConditions, valueField = 'price' }) {
  if (!timeOfUsable || Object.keys(timeOfUsable).length === 0) {
    return null;
  }

  const { value, description, conditions } = useTimeOfUsable(timeOfUsable, valueField);

  return (
    <span>
      {value}
      {' '}
      {description}
      {showConditions && (
      <>
        {' '}
        {conditions}
      </>
      )}
    </span>
  );
}

DetailedInline.propTypes = {
  timeOfUsable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  showConditions: PropTypes.bool.isRequired,
  valueField: PropTypes.string,
};

DetailedInline.defaultProps = {
  valueField: 'price',
};

export default DetailedInline;
